import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import copyText from '@/constants/utils/copy_text';
import { useToastStore } from '@/store';
import MemoryActionsDropdown from '@/components/Dropdowns/MemoryActionsDropdown';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

const isWeb = Platform.OS === 'web';

function MemoryHero({
  memories = [],
  onOpenAdd,
  isAdding = false,
  isCreating = false,
  onOpenBuilder,
  onOpenImport,
  onToggleSettings,
  settingsExpanded = false,
}) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const [isExporting, setIsExporting] = useState(false);
  const styles = useMemo(() => createStyles(colors, palette, isXS), [colors, palette, isXS]);

  const handleExport = useCallback(async () => {
    if (!memories.length) return;
    setIsExporting(true);
    try {
      const formatted = memories
        .map((m, idx) => {
          const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
          const sourceLabel =
            source === 'explicit' ? t('settings.memory.sourceExplicit')
              : source === 'dream' ? t('settings.memory.sourceDream')
              : source === 'auto' ? t('settings.memory.sourceAuto')
              : source === 'import' ? t('settings.memory.sourceImport')
              : t('pages.memory.manualCount');
          return `${idx + 1}. [${sourceLabel}] ${m.content}`;
        })
        .join('\n\n');

      await copyText(`# Gab AI Memories (${memories.length} total)\n\n${formatted}\n`);
      addToast(
        t('pages.memory.allMemoriesExported').replace('%COUNT%', memories.length),
        'success'
      );
    } catch {
      addToast(t('common.failedToCopy') || 'Failed to copy memories', 'error');
    } finally {
      setTimeout(() => setIsExporting(false), 1500);
    }
  }, [memories, addToast, t]);

  return (
    <View style={styles.container}>
      <View style={styles.titleRow}>
        <View style={styles.titleCopy}>
          <Text tag="h1" weight="semibold" style={styles.headline}>
            {t('pages.memory.title')}
          </Text>
          <Text size="s" color="secondary" style={styles.subtitle}>
            {t('pages.memory.subtitle')}
          </Text>
        </View>
        <View style={styles.actions}>
          <Pressable onPress={onToggleSettings} accessibilityRole="button"
            accessibilityLabel={t('pages.memory.settings')}
            accessibilityState={{ expanded: settingsExpanded }}
            {...(isWeb ? { 'aria-expanded': settingsExpanded } : {})}
            style={({ hovered }) => [styles.overflowButton, (hovered || settingsExpanded) && styles.overflowButtonHovered]}>
            <Icon name="sliders" size={16} color={colors.text_color_secondary} />
          </Pressable>
          <Button
            text={isAdding ? t('common.cancel') : t('pages.memory.addMemory')}
            prependIcon={isAdding ? 'xmark' : 'plus'}
            variant="secondary"
            size="medium"
            isDisabled={isCreating}
            onPress={onOpenAdd}
            iconColor={palette.onInk}
            textStyle={{ color: palette.onInk, fontWeight: '600' }}
            style={{ backgroundColor: palette.ink, borderRadius: 10 }}
            hoverBackgroundColor={palette.ink}
          />
          <MemoryActionsDropdown
            trigger={
              <Pressable accessibilityRole="button" accessibilityLabel={t('pages.memory.moreOptions')}
                style={({ hovered }) => [styles.overflowButton, hovered && styles.overflowButtonHovered]}>
                <Icon name="ellipsis" size={16} color={colors.text_color_primary} />
              </Pressable>
            }
            onOpenBuilder={onOpenBuilder}
            onOpenImport={onOpenImport}
            onExport={handleExport}
            canExport={memories.length > 0}
            exportLabel={isExporting ? t('pages.memory.copied') : t('pages.memory.exportMemories')}
          />
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, palette, isXS) =>
  StyleSheet.create({
    container: {
      width: '100%',
      paddingBottom: 24,
    },
    titleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: isXS ? 14 : 20,
    },
    titleCopy: {
      flex: 1,
      flexBasis: isXS ? '100%' : undefined,
      minWidth: 0,
      gap: 6,
    },
    headline: {
      fontSize: 32,
      lineHeight: 38,
      letterSpacing: -0.8,
      color: colors.text_color_primary,
    },
    subtitle: {
      maxWidth: 520,
      lineHeight: 22,
    },
    actions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexShrink: 0,
    },
    overflowButton: {
      width: 40,
      height: 40,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: palette.border,
      alignItems: 'center',
      justifyContent: 'center',
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    overflowButtonHovered: {
      backgroundColor: palette.hover,
    },
  });

export default React.memo(MemoryHero);
